import type { TaskDto } from '@bqcrm/shared';
import { Button, Checkbox, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { notifyError, notifyOk } from '../../notify';
import { ItemSelect, useStockItems } from './common';

/**
 * Materiał dodatkowy z biura (QA 11.2026 P2/P3): to samo zgłoszenie co z aplikacji ekipy (`POST /crew-demands`) — produkt
 * z katalogu albo pozycja opisowa (magazyn dopasuje produkt). Z karty sprawy — wybór zadania; z zapotrzebowania w magazynie —
 * zadanie z góry (magazynier nie musi mieć dostępu do karty sprawy).
 */
export function ExtraMaterialModal({ caseId, task, onClose }: { caseId?: string; task?: { id: string; label: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const tasksQ = useQuery({ queryKey: ['case-tasks', caseId], queryFn: () => api<TaskDto[]>(`/cases/${caseId}/tasks`), enabled: !task && !!caseId });
  const items = useStockItems();
  const open = (tasksQ.data ?? []).filter((t) => t.status !== 'anulowane' && t.scopes.length > 0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [mode, setMode] = useState<'katalog' | 'opis'>('katalog');
  const [itemId, setItemId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('szt.');
  const [quantity, setQuantity] = useState<number | string>(1);
  const [additional, setAdditional] = useState(false);
  const [comment, setComment] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const item = (items.data ?? []).find((i) => i.id === itemId);
  const tid = task?.id ?? taskId ?? (open.length === 1 ? open[0]!.id : null);
  const line =
    mode === 'katalog'
      ? { itemId, description: '', unit: item?.unit ?? 'szt.', quantity: Number(quantity), additional }
      : { itemId: null, description: name.trim(), unit: unit.trim() || 'szt.', quantity: Number(quantity), additional: false };
  const ready = !!tid && Number(quantity) > 0 && (mode === 'katalog' ? !!itemId : name.trim().length >= 2);
  const save = useMutation({
    mutationFn: () => api('/crew-demands', { method: 'POST', body: { taskId: tid, lines: [line], comment: comment.trim(), requestKey: key } }),
    onSuccess: () => {
      notifyOk(mode === 'opis' ? 'Zgłoszono materiał — magazyn dopasuje produkt' : 'Zgłoszono materiał — trafił do magazynu');
      void qc.invalidateQueries({ queryKey: ['case-materials'] });
      void qc.invalidateQueries({ queryKey: ['demands'] });
      void qc.invalidateQueries({ queryKey: ['demand'] });
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Materiał dodatkowy">
      <Stack>
        <Text size="sm" c="dimmed">
          Zgłoszenie trafia od razu do magazynu — jak zgłoszenie ekipy z aplikacji.
        </Text>
        {task ? (
          <TextInput label="Zadanie" value={task.label} readOnly />
        ) : (
          <Select label="Zadanie" data={open.map((t) => ({ value: t.id, label: `${t.number} — ${t.title}` }))} value={tid} onChange={setTaskId} required nothingFoundMessage="Brak zadań z zakresem prac" />
        )}
        <SegmentedControl
          value={mode}
          onChange={(v) => setMode(v as 'katalog' | 'opis')}
          data={[
            { value: 'katalog', label: 'Produkt z katalogu' },
            { value: 'opis', label: 'Pozycja opisowa' },
          ]}
        />
        {mode === 'katalog' ? (
          <>
            <ItemSelect label="Produkt" items={items.data ?? []} value={itemId} onChange={setItemId} required />
            <Checkbox label="Dodatkowo — ponad ilość z umowy" description="Zaznacz, gdy ten produkt jest już w potrzebach z umowy, a potrzeba więcej." checked={additional} onChange={(e) => setAdditional(e.currentTarget.checked)} />
          </>
        ) : (
          <Group grow align="flex-start">
            <TextInput label="Nazwa" description="Produktu nie ma w katalogu — magazyn go dopasuje" placeholder="Sprzęgło hydrauliczne DN25" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
            <TextInput label="Jednostka" value={unit} onChange={(e) => setUnit(e.currentTarget.value)} maw={110} />
          </Group>
        )}
        <NumberInput label={`Ilość${mode === 'katalog' && item ? ` (${item.unit})` : ''}`} value={quantity} onChange={setQuantity} min={0.001} decimalScale={3} required />
        <Textarea label="Komentarz" value={comment} onChange={(e) => setComment(e.currentTarget.value)} autosize minRows={2} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!ready}>
            Zgłoś do magazynu
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
